import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FC, useState } from "react";
import { useQuery } from "react-query";
import { getContainers } from "services/container";
import { searchByContainer } from "services/receipt";



// Base Models
export interface Container {
  id: string | number;
  name: string;
  quantity?: number;
}

// API Request/Response Models
export interface SearchByContainerQuery {
  containerId: string | number;
  from: string; // ISO date string (YYYY-MM-DD)
  to: string;   // ISO date string (YYYY-MM-DD)
}

export interface SearchByContainerRequest {
  query: SearchByContainerQuery;
}

// Raw API Response Models
export interface RawReceipt {
  receipt_number: string;
  amount_given: string; // String because it comes from API as string
  amount_received: string; // String because it comes from API as string
  containers: Container[];
}

export interface RawReceiptGroup {
  day: string; // ISO date string
  receipts: RawReceipt[];
}

export interface SearchByContainerResponse {
  data: RawReceiptGroup[];
}

// Processed/Formatted Models (used in component state)
export interface FormattedReceipt {
  containerName: string;
  missedAmount: number; // Calculated: amount_received - amount_given
  receiptNumber: string;
  day: string; // ISO date string
  amountGiven: number; // Parsed from string to number
  containers: Container[];
}

// Component State Models
export interface SearchByContainerState {
  selectedContainer: Container | null;
  open: boolean;
  fromDate: string | null; // ISO date string (YYYY-MM-DD)
  toDate: string | null;   // ISO date string (YYYY-MM-DD)
  showUnpaidOnly: boolean;
}

// Computed Values (derived from FormattedReceipt[])
export interface SearchSummary {
  totalContainerQuantity: number;
  sumOfMissedAmounts: number;
  resultsCount: number;
  formattedReceipts: FormattedReceipt[];
}

// Service Function Types
export type GetContainersFunction = () => Promise<Container[]>;

export type SearchByContainerFunction = (
  request: SearchByContainerRequest
) => Promise<SearchByContainerResponse>;

// React Query Keys
export type ContainersQueryKey = "containers";
export type ReceiptsQueryKey = [
  "receipts", 
  { 
    containerId: string | number | undefined;
    fromDate: string | null;
    toDate: string | null;
  }
];

// Event Handler Types
export interface SearchByContainerHandlers {
  handleContainerSelect: (event: any, value: Container | null) => void;
  handleSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFromDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClose: () => void;
}

// Filter Logic Types
export interface ReceiptFilter {
  showUnpaidOnly: boolean;
  selectedContainerId: string | number;
}

// Business Logic Notes:
// 1. missedAmount = amount_received - amount_given
// 2. When showUnpaidOnly is true, only receipts with amountGiven === 0 are shown
// 3. totalContainerQuantity sums all container quantities across all receipts
// 4. The component requires both fromDate and toDate to trigger the search query

export const SearchByContainer: FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const [selectedContainer, setSelectedContainer] = useState<any | null>(null);
  console.log({ selectedContainer });
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState<string | null>(null); // State for fromDate
  const [toDate, setToDate] = useState<string | null>(null); // State for toDate
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false); // State for unpaid filter

  const { data: containerData, isLoading: isContainerLoading } = useQuery({
    queryKey: "containers",
    queryFn: getContainers(),
  });

  const {
    data: receipts,
    isLoading,
    isError,
  } = useQuery(
    ["receipts", { containerId: selectedContainer?.id, fromDate, toDate }],
    {
      queryFn: () =>
        searchByContainer({
          query: {
            containerId: selectedContainer?.id,
            from: fromDate,
            to: toDate,
          },
        }),
      enabled: !!selectedContainer?.id && !!fromDate && !!toDate, // Only trigger query if both dates are selected
    }
  );

  const handleContainerSelect = (event: any, value: any) => {
    setSelectedContainer(value);
  };

  const handleSwitchChange = (event: any) => {
    setShowUnpaidOnly(event.target.checked); // Update switch state
  };

  // Calculate the filtered receipts based on the unpaid filter
  const formattedReceipts = receipts?.data
    ?.map((receipt: any) => {
      return (receipt.receipts || []).map((r: any) => {
        const container = r.containers.find(
          (container: any) => container.id === selectedContainer?.id
        );
        if (container) {
          const amountGiven = parseFloat(r.amount_given);
          const amountReceived = parseFloat(r.amount_received);
          const missedAmount = amountReceived - amountGiven;

          // Filter based on the unpaid switch state
          if (showUnpaidOnly && amountGiven !== 0) {
            return null; // Skip if "show unpaid only" is active and amountGiven is not zero
          }

          return {
            containerName: container.name,
            missedAmount,
            receiptNumber: r.receipt_number,
            day: receipt.day,
            amountGiven, // Add this for future reference
            containers: r?.containers || [],
          };
        }
        return null;
      });
    })
    .flat()
    .filter(Boolean); // Flatten to avoid nested arrays


    console.log({ formattedReceipts });

    const totalContainerQuantity = formattedReceipts?.reduce(
      (total: any, receipt: any) => total + receipt?.containers?.filter((container: any) => container.id === selectedContainer?.id).reduce((total: any, container: any) => total + (container?.quantity || 0), 0),
      0
    );

    console.log({ totalContainerQuantity, formattedReceipts });

  const sumOfMissedAmounts = formattedReceipts?.reduce(
    (total: any, receipt: any) => total + receipt.missedAmount,
    0
  );

  return (
    <Box
      sx={{
        mt: 3,
        ml: 2,
        width: "100%"
      }}
    >
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        size={isMobile ? "medium" : "large"}
        startIcon={<Search />}
      >
        Search by container
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 500,
          },
        }}
      >
        <DialogTitle>Search by container</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            options={containerData || []}
            getOptionLabel={(option: any) => option.name || ""}
            loading={isContainerLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by Container"
                placeholder="Type container name..."
              />
            )}
            onChange={handleContainerSelect}
          />

          {/* From Date Picker */}
          <TextField
            label="From"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setFromDate(e.target.value)}
          />

          {/* To Date Picker */}
          <TextField
            label="To"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setToDate(e.target.value)}
          />

          {/* Switch to show unpaid receipts */}
          <FormControlLabel
            control={
              <Switch checked={showUnpaidOnly} onChange={handleSwitchChange} />
            }
            label="Show only unpaid receipts"
          />

          {/* Show the results */}
          <Box mt={2}>
            {isLoading && <Typography>Loading...</Typography>}
            {isError && <Typography>Error loading receipts</Typography>}
            {formattedReceipts && formattedReceipts.length > 0 && (
              <List
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Typography variant="h6">
                  Results founded {formattedReceipts?.length}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#243642",
                    fontWeight: "bold",
                  }}
                >
                  {formattedReceipts[0]?.containerName} {totalContainerQuantity ? <span>: {totalContainerQuantity}</span> : null}
                </Typography>
                <Typography variant="h6" color="textPrimary" fontWeight={600}>
                  Total Missed Amount: {sumOfMissedAmounts.toFixed(2)} DT
                </Typography>
                {formattedReceipts.map(
                  (receipt: any, index: number) => {
                    console.log({ receipt });

                    return (
                      <ListItem
                        key={index}
                        sx={{
                          border: "1px solid",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          py: 2,
                          borderRadius: "8px",
                          borderColor: "#4CAF50", // Updated color for better contrast
                          cursor: "pointer",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)", // Slight scale on hover
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", // Add shadow effect on hover
                            borderColor: "#388E3C", // Darker green on hover
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <>
                              <Typography
                                variant="subtitle1" // Larger text for primary info
                                color="textPrimary" // Darker color for better contrast
                                fontWeight="bold" // Make the text bold for emphasis
                              >
                                Receipt Number: #{receipt.receiptNumber}
                              </Typography>
                            </>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="h6" // Larger size for missed amount
                                color="error" // Red color to signify importance
                                fontWeight="bold" // Bold text for emphasis
                              >
                                Missed: {receipt.missedAmount.toFixed(2)} DT
                              </Typography>
                              <Typography
                                variant="body2" // Smaller size for less important info
                                color="textSecondary" // Lighter color for subtlety
                              >
                                Day:{" "}
                                {new Date(receipt.day).toLocaleDateString()}
                              </Typography>
                            </>
                          }
                        />
                         <Divider />
                        <Box>
                        <Typography variant="subtitle1" sx={{ color: "primary", mb: 1.5, fontWeight: 600 }}>
                          Containers:
                        </Typography>
                        
                        {/* Get all unique containers across all receipts for this item */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          maxWidth: '100%'
                        }}>
                          {/* Extract and deduplicate containers */}
                          {receipt?.containers?.map((container: any) => container)?.filter(Boolean)?.map((container: any, idx: number) => (
                            <Chip
                              key={idx}
                              label={<>
                                {container?.name}
                                <b> x{container?.quantity}</b>
                              </>}
                              sx={{
                                backgroundColor: '#2C5282',
                                color: 'white',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#3182CE'
                                }
                              }}
                              icon={<Box 
                                component="span" 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: '#4FD1C5',
                                  mr: 1
                                }}
                              />}
                            />
                          ))}
                          
                          {/* Show message if no containers */}
                          {!receipt?.containers?.length && (
                            <Typography variant="body2" sx={{ color: '#A0AEC0', fontStyle: 'italic' }}>
                              No containers associated with this receipt
                            </Typography>
                          )}
                        </Box>
                        </Box>
                      </ListItem>
                    )
                  }
                )}
              </List>
            )}
            {formattedReceipts?.length === 0 && (
              <Typography>
                No receipts found for the selected container.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setSelectedContainer(null);
              setFromDate(null);
              setToDate(null);
              setShowUnpaidOnly(false); // Reset switch state on close
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
